import type { PitchFrame, FeedbackMessage } from '@shared/protocol';

const UDP_BEACON_PORT = 8766;
const WS_PORT = 8765;
const RECONNECT_DELAY = 3000;

type FeedbackCallback = (msg: FeedbackMessage) => void;
type StatusCallback = (connected: boolean) => void;

export class VeloraWS {
  private ws: WebSocket | null = null;
  private url: string;
  private onFeedback: FeedbackCallback;
  private onStatus: StatusCallback;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(host: string, onFeedback: FeedbackCallback, onStatus: StatusCallback) {
    this.url = `ws://${host}:${WS_PORT}`;
    this.onFeedback = onFeedback;
    this.onStatus = onStatus;
  }

  connect(): void {
    this.stopped = false;
    this._connect();
  }

  private _connect(): void {
    if (this.stopped) return;
    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => this.onStatus(true);

      this.ws.onmessage = (e) => {
        try {
          const msg: FeedbackMessage = JSON.parse(e.data);
          this.onFeedback(msg);
        } catch { /* ignore malformed */ }
      };

      this.ws.onclose = () => {
        this.onStatus(false);
        if (!this.stopped) {
          this.reconnectTimer = setTimeout(() => this._connect(), RECONNECT_DELAY);
        }
      };

      this.ws.onerror = () => this.ws?.close();
    } catch {
      if (!this.stopped) {
        this.reconnectTimer = setTimeout(() => this._connect(), RECONNECT_DELAY);
      }
    }
  }

  sendFrame(frame: PitchFrame): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }

  disconnect(): void {
    this.stopped = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = null;
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// LAN auto-discovery via UDP broadcast listener
export async function discoverDesktop(timeoutMs = 5000): Promise<string | null> {
  // In a Capacitor app we can't receive UDP directly in WebView.
  // Discovery falls back to manual IP entry; this function exists for
  // native plugin integration or a future UDP→HTTP bridge.
  return new Promise(resolve => setTimeout(() => resolve(null), timeoutMs));
}

export { WS_PORT, UDP_BEACON_PORT };
